"use client"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { motion } from "motion/react"
import { Exo_2 } from "next/font/google"
import Image from "next/image"
import Link from "next/link"
import { Book, Menu, Shapes, Sunset, Trees, Zap, Code2, BarChart3, Box, LineChart, ArrowUpRight, LucideIcon, Server, Camera, Shuffle, User, Expand, Sparkles, Palette, Users, CameraIcon, Megaphone, Shirt, FileText, HelpCircle, HeartHandshake, ChevronRight, Newspaper, VideoIcon, Ruler, Activity, List, Dna } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetClose,
  SheetContent,
} from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { useState } from "react"
import { CTAButton } from "./CTAButton";

const logoFont = Exo_2({ weight: ['300', '400', '700'], subsets: ['latin'] });

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header
      // initial={{ y: -20, opacity: 0 }}
      // animate={{ y: 0, opacity: 1 }}
      className="sticky top-0 z-50 flex py-2 bg-background/80 backdrop-blur-sm w-full justify-between px-4 lg:px-10"
    >
      <Link
        href={`/`}
        className="hover:opacity-80 transition-all text-3xl self-center"
        aria-label="homepage"
      >
        <div className="flex items-center gap-2">
          <Image
            src={'/logo-enhanced.svg'}
            alt={'fashn-logo'}
            width={'30'}
            height={'30'}
          />
          <h2 className={cn(logoFont.className, 'font-semibold text-2xl')}>
            FASHN<span className="font-light">AI</span>
          </h2>
        </div>
      </Link>

      <NavBar />

      <div className="flex items-center gap-4">
        <CTAButton
          location="navbar_desktop"
          variant="button"
          size="lg"
          className="hidden md:inline-flex"
          showArrow={false}
          ariaLabel="Go to app"
        >
          Go to app
        </CTAButton>

        <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(true)}>
          <Menu className="h-6 w-6" />
          <span className="sr-only">Open mobile menu</span>
        </Button>
      </div>

      <MobileMenu open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
    </header>
  )
}

export interface NavigationItem {
  title: string
  href: string
  description?: string
  icon?: LucideIcon
  external?: boolean
}

export interface NavigationSection {
  title?: string
  items: NavigationItem[]
}

export interface NavigationConfig {
  mainNav: {
    title: string
    href?: string
    sections?: NavigationSection[]
    external?: boolean
  }[]
}

export const navigationConfig: NavigationConfig = {
  mainNav: [

    {
      title: "Products",
      sections: [
        {
          title: "FASHN Platform",
          items: [
            {
              title: "App",
              href: "/products/web-app",
              description: "Create beautiful on-model visuals",
              icon: Box,
            },
            {
              title: "API",
              href: "/products/api",
              description: "Integrate virtual try-on into your app",
              icon: Server,
            },
          ],
        },
        {
          title: "AI Tools",
          items: [
            {
              title: "Try-On",
              href: "/virtual-try-on",
              description: "Learn about our proprietary AI model",
              icon: Shirt,
            },
            {
              title: "Model Swap",
              href: "/model-swap",
              description: "Replace just the model in a photo",
              icon: Shuffle,
            },
            {
              title: "Model Creation",
              href: "/model-creation",
              description: "Create AI fashion models",
              icon: User,
            },
            {
              title: "Consistent Models",
              href: "/consistent-models",
              description: "Create AI models with recognizable consistency",
              icon: Dna,
            },
            {
              title: "Short Videos",
              href: "/short-videos",
              description: "Add motion to your try-ons",
              icon: VideoIcon,
            },
            // {
            //   title: "Background Extender",
            //   href: "#",
            //   description: "Track user engagement",
            //   icon: Expand,
            // },
          ],
        },
      ],
    },
    {
      title: "Solutions",
      sections: [
        {
          title: "Use Cases",
          items: [
            {
              title: "Virtual Fashion Photoshoots",
              href: "/virtual-fashion-photoshoots",
              description: "Produce content for fashion brands",
              icon: Camera,
            },
            {
              title: "Virtual Fitting Rooms",
              href: "/virtual-fitting-rooms",
              description: "Interactive clothing try-on for e-commerce",
              icon: Ruler,
            },
            // {
            //   title: "Design",
            //   href: "#",
            //   description: "Launch campaigns fast",
            //   icon: Palette,
            // },
          ],
        },
        // {
        //   title: "Users",
        //   items: [
        //     {
        //       title: "Designers",
        //       href: "#",
        //       description: "Automate away repetition",
        //       icon: Users,
        //     },
        //     {
        //       title: "Photographers",
        //       href: "#",
        //       description: "Deploy for every idea",
        //       icon: CameraIcon,
        //     },
        //     {
        //       title: "Developers",
        //       href: "#",
        //       description: "Deploy for every idea",
        //       icon: Code2,
        //     },
        //     {
        //       title: "Marketers",
        //       href: "#",
        //       description: "Deploy for every idea",
        //       icon: Megaphone,
        //     },
        //   ],
        // },
      ],
    },
    {
      title: "Pricing",
      href: "/pricing",
      external: true,
    },
    {
      title: "Resources",
      sections: [
        {
          items: [
            {
              title: "About Us",
              href: "/about",
              description: "Our mission at FASHN",
              icon: HeartHandshake,
            },
            {
              title: "Careers",
              href: "/careers",
              description: "Join the FASHN team",
              icon: Users,
            },
            {
              title: "Blog",
              href: "/blog",
              description: "Latest news from FASHN",
              icon: Newspaper,
            },
            {
              title: "Documentation",
              href: "https://docs.fashn.ai/",
              description: "Learn how to use the API",
              icon: FileText,
            },
            {
              title: "Changelog",
              href: "/changelog",
              description: "FASHN updates and improvements",
              icon: List,
            },
            {
              title: "Status",
              href: "https://status.fashn.ai/",
              description: "Check the system status and uptime",
              icon: Activity,
            },
          ],
        },
      ],
    },
  ],
}


function NavBar() {
  return (
    <NavigationMenu className="hidden md:flex justify-center self-center">
      <NavigationMenuList className="space-x-6">
        {navigationConfig.mainNav.map((item) => (
          <NavigationMenuItem key={item.title}>
            {item.sections ? (
              <>
                <NavigationMenuTrigger>{item.title}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div
                    className={`grid gap-10 p-6 ${item.sections.length > 1 ? "w-[650px] grid-cols-2" : "w-[400px]"}`}
                  >
                    {item.sections.map((section, i) => (
                      <div key={section?.title || `no-title-${i}`} className="flex flex-col gap-4 items-start">
                        {section?.title && <h3 className="tracking-wide font-medium opacity-80">{section.title}</h3>}
                        <div className="grid gap-4 w-full">
                          {section.items.map((subItem) => (
                            <NavigationMenuLink key={subItem.title} asChild>
                              <Link
                                href={subItem.href ?? "#"}
                                className={cn("group grid w-full items-start gap-4 hover:bg-muted p-3 rounded-md",
                                  'grid-cols-[24px_1fr]'
                                )}
                              >
                                <div className="p-1.5 bg-gradient-to-br from-background to-primary border border-primary  w-fit rounded-lg shadow-lg">{subItem.icon && <subItem.icon className="h-4 w-4" />}</div>
                                <div>
                                  <div className="text-sm font-medium leading-none">
                                    {subItem.title}
                                  </div>
                                  {subItem.description && (
                                    <p className="line-clamp-2 mt-1 text-xs text-muted-foreground group-hover:text-foreground transition-all">
                                      {subItem.description}
                                    </p>
                                  )}
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </NavigationMenuContent>
              </>
            ) : (
              <Link href={item.href ?? "#"} legacyBehavior passHref>
                <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                  <span className="flex items-center gap-1">
                    {item.title}
                  </span>
                </NavigationMenuLink>
              </Link>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  )
}


function MobileMenu({ open, onClose }: any) {

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[300px] sm:w-[400px] border-0">

        <div className="mt-6 flex flex-col gap-4">
          {navigationConfig.mainNav.map((item, index) => (
            <h3 key={item.title}>
              {item.sections ? (
                <Accordion type="single" collapsible>

                  <AccordionItem value="item-1">

                    <AccordionTrigger className="font-light">{item.title}</AccordionTrigger>
                    <AccordionContent>
                      {item.sections.map((s, i) => (
                        <div key={s?.title || `title-${i}`}>

                          <ul className="space-y-4 py-2">
                            {s.items.map((item: NavigationItem) => (
                              <li key={item.title} className="flex gap-2 items-center">
                                {item?.icon && <item.icon className="h-4 w-4" />}
                                <SheetClose asChild>
                                  <Link href={item.href}>
                                    {item.title}
                                  </Link>
                                </SheetClose>
                              </li>
                            ))}
                          </ul>

                        </div>
                      ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              ) : (
                <SheetClose asChild>
                  <Link
                    href={item.href ?? "#"}
                    className="flex items-center justify-between p-2 rounded-md"
                  >
                    <span>{item.title}</span>
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </SheetClose>
              )}
            </h3>
          ))}
          <SheetClose asChild>
            <CTAButton
              location="navbar_mobile"
              variant="button"
              size="lg"
              className="mt-4 w-full"
              showArrow={false}
            >
              Go to app
            </CTAButton>
          </SheetClose>
        </div>
      </SheetContent>
    </Sheet>
  )
}